import * as React from 'react'
import { Drawer, Switch } from 'antd'
import { SecurityRole, PermissionNames } from '@getgreenline/homi-shared'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { CompaniesModels } from '@getgreenline/companies'

interface Props {
  onClose: () => void
  onUpdate: (permissionId: number) => void
  securityRole: SecurityRole
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

const categoryIndexes = [
  {
    index: 0,
    name: 'Dashboard - General',
    permissions: [
      PermissionNames.CAN_USE_ADMINISTRATIVE_DASHBOARD,
      PermissionNames.CAN_EDIT_COMPANY_DETAILS,
      PermissionNames.CAN_MANAGE_INTEGRATIONS,
      PermissionNames.CAN_MANAGE_EMPLOYEES,
      PermissionNames.CAN_MANAGE_COMPLIANCE_REPORTING,
    ],
  },
  {
    index: 5,
    name: 'Dashboard - Products and inventory',
    permissions: [
      PermissionNames.CAN_EDIT_PRODUCTS,
      PermissionNames.CAN_BULK_EDIT_PRODUCTS,
      PermissionNames.CAN_BULK_EDIT_PRICE_OVERRIDES,
      PermissionNames.CAN_BULK_EDIT_DETAILS_OVERRIDES,
      PermissionNames.CAN_VIEW_DISCOUNTS,
      PermissionNames.CAN_EDIT_DISCOUNTS,
      PermissionNames.CAN_EDIT_CATEGORIES,
      PermissionNames.CAN_VIEW_INVENTORY,
      PermissionNames.CAN_EDIT_INVENTORY,
      PermissionNames.CAN_VIEW_AUDITS,
      PermissionNames.CAN_EDIT_AUDITS,
      PermissionNames.CAN_VIEW_TRANSFERS,
      PermissionNames.CAN_EDIT_TRANSFERS,
      PermissionNames.CAN_VIEW_VENDORS,
      PermissionNames.CAN_EDIT_VENDORS,
      PermissionNames.CAN_MANAGE_WOOCOMMERCE,
    ],
  },
  {
    index: 22,
    name: 'Dashboard - Customers',
    permissions: [
      PermissionNames.CAN_VIEW_CUSTOMERS,
      PermissionNames.CAN_ADD_CUSTOMERS,
      PermissionNames.CAN_EDIT_CUSTOMERS,
      PermissionNames.CAN_VIEW_LOYALTY_TIERS,
      PermissionNames.CAN_EDIT_LOYALTY_TIERS,
      PermissionNames.CAN_VIEW_CUSTOMER_TOTAL_SPEND,
      PermissionNames.CAN_ADJUST_LOYALTY_BALANCE,
      PermissionNames.CAN_MANAGE_OUTREACH,
    ],
  },
  {
    index: 29,
    name: 'Dashboard - Sales',
    permissions: [
      PermissionNames.CAN_VIEW_SALES,
      PermissionNames.CAN_EDIT_SALES,
      PermissionNames.CAN_MANAGE_PARKED_SALES,
    ],
  },
  {
    index: 29,
    name: 'Dashboard - Purchase orders',
    permissions: [PermissionNames.CAN_VIEW_PURCHASE_ORDERS],
  },
  {
    index: 33,
    name: 'Dashboard - Reports',
    permissions: [
      PermissionNames.CAN_VIEW_REPORTS,
      PermissionNames.CAN_UPDATE_INVENTORY_LOGS,
      PermissionNames.CAN_EDIT_EMPLOYEE_HOURS,
      PermissionNames.CAN_VIEW_BROADCASTS,
      PermissionNames.CAN_EDIT_BROADCASTS,
    ],
  },
  {
    index: 36,
    name: 'POS',
    permissions: [
      PermissionNames.CAN_START_AND_END_SHIFTS,
      PermissionNames.CAN_ASSIGN_DISCOUNTS,
      PermissionNames.CAN_ASSIGN_MANAGER_DISCOUNTS,
      PermissionNames.CAN_REFUND_SALES,
      PermissionNames.CAN_PERFORM_NO_SALE,
      PermissionNames.CAN_MAKE_NEGATIVE_INVENTORY_SALES,
      PermissionNames.CAN_SET_BULK_PRODUCT_PRICES,
    ],
  },
]

const PermissionsDrawer: React.FC<Props & InjectedProps> = inject('currentCompanyStore')(
  observer((props) => {
    const { securityRole, currentCompanyStore } = props
    const drawerWidth = Math.min(500, window.innerWidth)

    const permissionMap = new Map(
      securityRole.permissions.map((permission) => [permission.permission.name, permission]),
    )

    return (
      <Drawer
        visible={true}
        title={securityRole.name || 'Security role'}
        width={drawerWidth}
        onClose={props.onClose}
      >
        <table className='table table-sm mb-4'>
          <tbody>
            {categoryIndexes.map((category, index) => {
              return (
                <React.Fragment key={index}>
                  <tr className='table-secondary'>
                    <td colSpan={2}>{category.name}</td>
                  </tr>
                  {category.permissions.map((permission) => {
                    const matchingPermission = permissionMap.get(permission)

                    if (!matchingPermission) return

                    if (
                      matchingPermission.permission.name ===
                        PermissionNames.CAN_ADJUST_LOYALTY_BALANCE &&
                      !currentCompanyStore?.company?.permissions.canAccessLoyalty
                    )
                      return

                    const isCompanyProvinceON =
                      currentCompanyStore?.company?.province === CompaniesModels.Province.ON

                    if (
                      matchingPermission.permission.name ===
                        PermissionNames.CAN_MANAGE_COMPLIANCE_REPORTING &&
                      (!currentCompanyStore?.canUseOCSCompliance || !isCompanyProvinceON)
                    )
                      return

                    if (
                      [
                        PermissionNames.CAN_VIEW_BROADCASTS,
                        PermissionNames.CAN_EDIT_BROADCASTS,
                      ].includes(matchingPermission.permission.name) &&
                      !currentCompanyStore?.canUseBroadcasts
                    )
                      return

                    return (
                      <tr key={permission}>
                        <td>
                          <b>{permission}</b>
                          <div className='text-muted'>
                            {matchingPermission.permission.description}
                          </div>
                        </td>
                        <td className='text-right'>
                          <Switch
                            checked={matchingPermission.state}
                            onChange={() => props.onUpdate(matchingPermission.permission.id)}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
        <div style={{ padding: '1rem' }} />
      </Drawer>
    )
  }),
)

export { PermissionsDrawer }
